import React, {useEffect, useMemo, useRef, useState} from 'react';
import useOutsideClick from 'hooks/useOutsideClick';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import styled from 'styled-components';
import IconDropdown from 'assets/icon-dropdown-dark.svg';
import DatePicker from 'components/feature/DatePicker';
import {ThemeProvider} from 'components/Datepicker/DatePickerContext';
import {ButtonPrimary} from 'components/buttons';
import {TIME_PERIODS, TIME_PERIOD_OPTIONS} from '../constants';

const Wrap = styled.div`
  position: relative;
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  background: transparent;
  border: 0.5px solid #d5d8de;
  border-radius: 8px;
  padding: 18px 25px 17px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #2f3d48;
  cursor: pointer;
  min-width: 157px;
  justify-content: end;

  span {
    font-family: 'Poppins', sans-serif;
    color: #2f3d48;
    font-weight: 400;
    font-size: 14px;
    line-height: 150%;
    text-align: right;
  }

  img {
    width: 12px;
    height: 12px;
    display: block;
    transition: transform 150ms ease;
  }

  &.isOpen img {
    transform: rotate(180deg);
  }
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 5px 10px 0px #00000033;
  z-index: 20;
  overflow: hidden;
`;

const PresetsList = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 168px;
  padding-right: 8px;
`;

const Item = styled.button`
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: none;
  background: transparent;
  font-family: 'Poppins', sans-serif;
  color: #2f3d48;
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f3f4f6;
  }

  &.isActive {
    font-weight: 700;
    background: #f7f8fa;
  }
`;

const formatRange = (from, to) => {
  if (!from || !to) return null;
  const start = moment.utc(from);
  const end = moment.utc(to);
  if (start.isSame(end, 'day')) {
    return start.format('MMM D, YYYY');
  }
  return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
};

const isCustomFilled = (customRange) =>
  Boolean(customRange?.[0]) && Boolean(customRange?.[1]);

const toPickerRange = (range) => {
  if (!range) return [null, null];
  return [
    range[0] ? moment.utc(range[0]).format() : null,
    range[1] ? moment.utc(range[1]).format() : null
  ];
};

const TimePeriodSelector = ({value, customRange, onChange}) => {
  const [open, setOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [pendingRange, setPendingRange] = useState(toPickerRange(customRange));
  const [, setPickerSelection] = useState([]);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setShowCalendar(false);
      setPendingRange(toPickerRange(customRange));
    }
  }, [open, customRange]);

  useOutsideClick(wrapRef, () => setOpen(false), open);

  const selected = TIME_PERIOD_OPTIONS.find((o) => o.id === value);

  const triggerLabel = useMemo(() => {
    if (showCalendar) {
      if (!isCustomFilled(pendingRange)) return 'Select date range';
      const start = moment.utc(pendingRange[0]).format('MMM D, YYYY');
      const end = moment.utc(pendingRange[1]).format('MMM D, YYYY');
      return `${start} - ${end}`;
    }
    if (value === TIME_PERIODS.CUSTOM && isCustomFilled(customRange)) {
      return formatRange(customRange[0], customRange[1]);
    }
    return selected?.label ?? 'Select';
  }, [showCalendar, pendingRange, value, customRange, selected]);

  const handleSelect = (id) => {
    if (id === TIME_PERIODS.CUSTOM) {
      setShowCalendar(true);
      return;
    }
    onChange({timePeriod: id});
    setOpen(false);
  };

  const handleApplyCustom = () => {
    if (!isCustomFilled(pendingRange)) return;
    onChange({
      timePeriod: TIME_PERIODS.CUSTOM,
      from: moment.utc(pendingRange[0]).format('YYYY-MM-DDTHH:mm:ss'),
      to: moment.utc(pendingRange[1]).format('YYYY-MM-DDTHH:mm:ss')
    });
    setOpen(false);
  };

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        type='button'
        className={cn({isOpen: open})}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span>{triggerLabel}</span>
        <img src={IconDropdown} alt='' />
      </Trigger>
      {open && (
        <Panel>
          <div className='Say2EatDrop'>
            <div className='DropDown DropFilterDate'>
              <div className='DropDownHolder'>
                <PresetsList>
                  {TIME_PERIOD_OPTIONS.map((option) => (
                    <Item
                      key={option.id}
                      type='button'
                      className={cn({
                        isActive: showCalendar
                          ? option.id === TIME_PERIODS.CUSTOM
                          : option.id === value
                      })}
                      onClick={() => handleSelect(option.id)}
                    >
                      {option.label}
                    </Item>
                  ))}
                </PresetsList>
                {showCalendar && (
                  <div className='DropDownDatepicker'>
                    <ThemeProvider>
                      <DatePicker
                        setSelectedDate={setPickerSelection}
                        dateValue={pendingRange}
                        datepickerOnChange={setPendingRange}
                        disableFuture
                        disablePast={false}
                        minDate={new Date(2000, 0, 1)}
                        dateHolidays={false}
                      />
                    </ThemeProvider>
                    <div className='DropDownActions'>
                      <ButtonPrimary
                        className='ButtonSmall'
                        onClick={handleApplyCustom}
                        disabled={!isCustomFilled(pendingRange)}
                      >
                        <span>Apply</span>
                      </ButtonPrimary>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Panel>
      )}
    </Wrap>
  );
};

TimePeriodSelector.propTypes = {
  value: PropTypes.string.isRequired,
  customRange: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired
};

TimePeriodSelector.defaultProps = {
  customRange: null
};

export default TimePeriodSelector;
